/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import moment from 'moment';
import 'moment-timezone';
import Tabs from '../controls/Tabs';
import SearchInput from '../controls/SearchInput';
import Button from '../controls/Button';
import AlertsTable from '../components/AlertsTable';
import DevicesAlertActivity from '../components/DevicesAlertActivity';
import { useData } from '../context/data.context';
import { getAlertsWithSearch, updateAlert } from '../services/dashboard-service';
import {
  Alert,
  AlertStatus,
  ParametersType,
  TimeSpan,
  TimeSpanDateAttributes
} from '../constants/telosair-interfaces';
import FilterDeviceAndParameters from '../controls/FilterDeviceAndParameters';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';
import DropdownControl from '../controls/DropdownControl';
import DateRange from '../components/DateRange';
import useStorageState from '../hooks/useStorageState';
import { handleTimeSpanChange } from '../_helpers/dashboardHelper';
import PlanGate from '../components/PlanGate';

const TAB_ALERTS = 'alerts';
const TAB_NOTES = 'notes';

const ALERT_DATA_STATUS = [
  { name: 'Pending', id: 'pending' },
  { name: 'Resolved', id: 'resolved' },
  { name: 'Dismissed', id: 'dismiss' },
  { name: 'All', id: 'all' }
];

/**
 * Alert Page Component. The Goal of these page is to show all the alerts.
 * @returns - Alerts Page.
 */
export default function AlertsPage(): ReactElement {
  const PAGE_TABS = [
    { id: TAB_ALERTS, name: 'Alerts' }
    // { id: TAB_NOTES, name: 'Notes' }
  ];

  const { allAlerts, updateAlertData } = useData();
  const [dataStatus, setDataStatus] = useState<AlertStatus>('all');
  const [timeSpanDate, setTimeSpanDate] = useState<TimeSpanDateAttributes | null>(null);
  const timeSpanFilter = getAppliedFilter(Filters.alertTimeSpan)?.data;
  const [timeSpan, setTimeSpan] = useStorageState<TimeSpan | null>(Filters.alertTimeSpan, null);
  const [selectedDevices, setSelectedDevices] = useStorageState<{ label: string; value: number }[]>(
    Filters.alertDevices,
    []
  );
  const [selectedParameters, setSelectedParameters] = useStorageState<ParametersType[]>(
    Filters.alertParameters,
    []
  );
  const [selectedTab, setSelectedTab] = useState(TAB_ALERTS);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [searchText, setSearchText] = useState('');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkedAlerts, setCheckedAlerts] = useState<string[]>([]);

  useEffect(() => {
    if (!timeSpan) {
      return;
    }
    if (timeSpanDate === null) {
      const time =
        timeSpanFilter === TimeSpan.Custom
          ? getAppliedFilter(Filters.alertTimeSpanCustom)?.data
          : handleTimeSpanChange(timeSpan, timeSpanDate);
      setTimeSpanDate({ ...time });
    }
  }, [timeSpan, timeSpanDate]);

  type fetchDataProps = {
    searchText: string;
    selectedParameters: string[];
    selectedDevices: number[];
    timeSpanDate: TimeSpanDateAttributes | null;
    dataStatus: AlertStatus;
  };
  const debouncedFetchData = _.debounce(async (props: fetchDataProps) => {
    try {
      const response = await getAlertsWithSearch({
        searchString: props.searchText,
        devices: props.selectedDevices,
        parameters: props.selectedParameters,
        startDate: props.timeSpanDate?.startDate,
        endDate: props.timeSpanDate?.endDate,
        statuses: props.dataStatus !== 'all' ? [props.dataStatus] : []
      });
      setAlerts(response.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (searchText.length !== 0) {
      setLoading(true);
      debouncedFetchData({
        searchText,
        selectedParameters,
        selectedDevices: selectedDevices.map((device) => device.value),
        timeSpanDate,
        dataStatus
      });
    } else {
      setLoading(true);
      debouncedFetchData({
        searchText: '*',
        selectedParameters,
        selectedDevices: selectedDevices.map((device) => device.value),
        timeSpanDate,
        dataStatus
      });
    }

    return () => {
      debouncedFetchData.cancel();
    };
  }, [searchText, allAlerts, selectedParameters, selectedDevices, timeSpanDate, dataStatus]);

  /**
   * Function to apply devices filter.
   * @param devices - Selected devices.
   */
  const onSelectDevices = (devices: { label: string; value: number }[]): void => {
    setSelectedDevices(devices);
    setAppliedFilter({ key: Filters.alertParameters, data: devices });
  };

  /**
   * Function to apply devices filter.
   * @param parameters - Selected devices.
   */
  const onSelectParameters = (parameters: ParametersType[]): void => {
    setSelectedParameters(parameters);
    setAppliedFilter({ key: Filters.dataParameters, data: parameters });
  };

  /**
   * Calls handleTimeSpanChange helper and also sets time span.
   * @param span - Time span selected.
   */
  const handleTimeChange = (span: string | TimeSpan | null): void => {
    if (!span) {
      setTimeSpan(null);
      setTimeSpanDate(null);
      return;
    }
    setTimeSpan(span as TimeSpan);
    if (span !== TimeSpan.Custom) {
      setTimeSpanDate({ ...handleTimeSpanChange(span, timeSpanDate) });
    }
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleChekboxSelectedItems = async (status: AlertStatus): Promise<void> => {
    // eslint-disable-next-line no-restricted-syntax
    for (const alert of checkedAlerts) {
      // eslint-disable-next-line no-await-in-loop
      const updatedAlert = await updateAlert({ status }, alert);
      updateAlertData(updatedAlert.data);
    }
    toast.success('Alert updated successfully');
  };

  return (
    <PlanGate requiredPlan="Advanced">
      <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4">
        <div className="flex items-center space-x-4">
          <div className="min-w-[21.75rem]">
            <SearchInput
              placeholder="Search for Devices and Groups"
              textValue={searchText}
              setText={setSearchText}
            />
          </div>
          <FilterDeviceAndParameters
            selectedDevices={selectedDevices}
            selectedParameters={selectedParameters}
            onSelectDevices={onSelectDevices}
            onSelectParameters={onSelectParameters}
            label="Filters"
            position="left"
            maxParameterCount={10}
          />
          <DateRange
            hansNoneDate
            theme="bg-light"
            onChangeTimeFilter={handleTimeChange}
            timeSpan={timeSpan}
            timeSpanDate={timeSpanDate}
            setTimeSpanDate={setTimeSpanDate}
          />
          <DropdownControl
            controlPlaceholder={ALERT_DATA_STATUS.filter((item) => item.id === dataStatus)[0]?.name}
            options={ALERT_DATA_STATUS}
            theme="bg-light"
            onSelect={(option) => setDataStatus(option.id)}
          />
          <DropdownControl
            controlPlaceholder="Actions"
            disabled={checkedAlerts.length === 0}
            position="right"
            options={[
              { name: 'Resolve', id: 'resolved' },
              { name: 'Dismiss', id: 'dismiss' }
            ]}
            theme="bg-light"
            onSelect={(option) => handleChekboxSelectedItems(option.id)}
          />
          {/* <Filter position="left" />
            <DatePickerDropdown /> */}
        </div>
        <NavLink to="/alert-rules" className="mr-4">
          <Button theme="secondary-color" size="sm" name="Alert Rules" isBlockButton />
        </NavLink>
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-8 gap-0 xl:gap-4 my-4 mx-0 lg:mx-0">
        <div className="col-span-2 xl:col-span-6 overflow-x-scroll mb-4">
          {selectedTab === TAB_ALERTS && (
            <AlertsTable
              checkedAlerts={checkedAlerts}
              setCheckedAlerts={setCheckedAlerts}
              onSelectAlert={(alert) => setSelectedAlert(alert)}
              allAlerts={alerts}
            />
          )}
        </div>
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 col-span-2 xl:col-span-2 gap-4">
            <div className="col-span-1 md:col-span-1 lg:col-span-6 xl:col-span-12">
              <div className="flex h-fit">
                <DevicesAlertActivity selectedAlert={selectedAlert} alerts={alerts} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
    </PlanGate>
  );
}
